/**
 * src/shell/agents/services/VoiceIntentClassifier.ts
 *
 * Speech Intent Classifier & Voice Tool Dispatcher.
 * Maps transcribed voice utterances into:
 * 1. Knowledge Queries (llmwiki / noodle2)
 * 2. Canvas AST Mutations (Generative UI)
 * 3. Structured Hardware Mesh Tool Invocations (Door unlocks, node quarantine)
 * 4. Conversational Chat
 */

import { IVoiceToolCall } from '../types/voice-types';

export type PipelineCategory =
  | 'KNOWLEDGE_QUERY'
  | 'CANVAS_MUTATION'
  | 'MESH_TOOL_INVOCATION'
  | 'CONVERSATIONAL_CHAT';

export interface ClassifiedVoiceIntent {
  category: PipelineCategory;
  toolCall?: IVoiceToolCall;
  queryOrInstruction: string;
  confidence: number;
}

export class VoiceIntentClassifier {
  private static instance: VoiceIntentClassifier;

  private constructor() {}

  public static getInstance(): VoiceIntentClassifier {
    if (!VoiceIntentClassifier.instance) {
      VoiceIntentClassifier.instance = new VoiceIntentClassifier();
    }
    return VoiceIntentClassifier.instance;
  }

  public classify(transcript: string): ClassifiedVoiceIntent {
    const lower = transcript.toLowerCase();

    // 1. Mesh Hardware / Operational Tools
    if (lower.includes('unlock') && (lower.includes('door') || lower.includes('room') || lower.includes('gate'))) {
      return {
        category: 'MESH_TOOL_INVOCATION',
        toolCall: {
          id: `call_${Date.now()}`,
          name: 'mesh:door:unlock',
          parameters: { doorTarget: this.extractDoorName(transcript) },
          requiresStepUp: false,
          requiredRole: 'ordinary_user',
          rationale: 'Voice-initiated proximity unlock request',
        },
        queryOrInstruction: transcript,
        confidence: 0.95,
      };
    }

    if (lower.includes('quarantine') || (lower.includes('isolate') && lower.includes('device'))) {
      return {
        category: 'MESH_TOOL_INVOCATION',
        toolCall: {
          id: `call_${Date.now()}`,
          name: 'mesh:device:quarantine',
          parameters: { deviceId: '04:A1:B2:FF:99', reason: 'Unrecognized hardware anomaly' },
          requiresStepUp: true,
          requiredRole: 'mesh_owner',
          rationale: 'Privileged device quarantine command requires biometric confirmation',
        },
        queryOrInstruction: transcript,
        confidence: 0.94,
      };
    }

    if (lower.includes('rebalance') && (lower.includes('credit') || lower.includes('node') || lower.includes('pool'))) {
      return {
        category: 'MESH_TOOL_INVOCATION',
        toolCall: {
          id: `call_${Date.now()}`,
          name: 'mesh:node:rebalance',
          parameters: { targetNode: 'Node-7', amount: 500 },
          requiresStepUp: true,
          requiredRole: 'mesh_owner',
          rationale: 'Liquidity reallocation requires Step-Up authorization',
        },
        queryOrInstruction: transcript,
        confidence: 0.92,
      };
    }

    if (lower.includes('emergency lockdown') || (lower.includes('lockdown') && lower.includes('cluster'))) {
      return {
        category: 'MESH_TOOL_INVOCATION',
        toolCall: {
          id: `call_${Date.now()}`,
          name: 'agent:emergency:lockdown',
          parameters: { scope: 'GLOBAL_CLUSTER' },
          requiresStepUp: true,
          requiredRole: 'superadmin',
          rationale: 'Emergency Cluster Lockdown requires Glass Break Superadmin authorization',
        },
        queryOrInstruction: transcript,
        confidence: 0.98,
      };
    }

    // 2. Canvas Mutations
    if (
      lower.startsWith('make') ||
      lower.startsWith('change') ||
      lower.startsWith('add') ||
      lower.startsWith('remove') ||
      lower.includes('heatmap') ||
      lower.includes('sankey')
    ) {
      return {
        category: 'CANVAS_MUTATION',
        toolCall: {
          id: `call_${Date.now()}`,
          name: 'canvas:mutate',
          parameters: { instruction: transcript },
          requiresStepUp: false,
          requiredRole: 'ordinary_user',
          rationale: 'Generative Canvas layout refinement',
        },
        queryOrInstruction: transcript,
        confidence: 0.9,
      };
    }

    // 3. Knowledge Graph Queries
    if (lower.includes('why') || lower.includes('adr') || lower.includes('benchmark') || lower.includes('how does')) {
      return {
        category: 'KNOWLEDGE_QUERY',
        toolCall: {
          id: `call_${Date.now()}`,
          name: 'knowledge:query',
          parameters: { query: transcript },
          requiresStepUp: false,
          requiredRole: 'ordinary_user',
          rationale: 'Multi-hop knowledge retrieval query',
        },
        queryOrInstruction: transcript,
        confidence: 0.93,
      };
    }

    // 4. Conversational Chat
    return {
      category: 'CONVERSATIONAL_CHAT',
      queryOrInstruction: transcript,
      confidence: 0.85,
    };
  }

  private extractDoorName(text: string): string {
    const lower = text.toLowerCase();
    if (lower.includes('server')) return 'Server Room 4B';
    if (lower.includes('vault')) return 'Vault Alpha';
    if (lower.includes('entrance') || lower.includes('main')) return 'Main Entrance Gate';
    return 'Assigned Office Door';
  }
}

export const voiceIntentClassifier = VoiceIntentClassifier.getInstance();
